/**
 * Werkstatt-Zweig „Bestand" — Karten-Aggregat pro Monat × Mandant.
 *
 * Kanonische Quelle für Counter-Anzeigen (Kanban-Tab-Badges, Mobile-Tabs,
 * Inbox-Badges). Sichtbar gemachte Glass-Box-Regel: jeder UI-Counter MUSS
 * gegen `/api/cards/counts` rechnen, nicht clientseitig aus einer bereits
 * gefilterten Card-Liste.
 *
 * Auslöser für diesen Zweig: am 2026-05-19 zeigten Kanban-Tabs außer „Alle"
 * Counter=0 für nicht-aktive Kategorien — Drift weil typeCounts in
 * KanbanPage:173 aus dem server-gefilterten cards[] gerechnet wurde. Mit
 * dieser Werkstatt-Sicht wäre der Drift im Drift-Check-Step (Step 2) als
 * "Aggregat ≠ Liste" markiert worden.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DetailModal } from "../components/DetailModal";
import { WerkstattBranchHeader } from "../components/werkstatt/WerkstattBranchHeader";
import { PipelineStepList, type PipelineStep } from "../components/SettingsTestOcr";
import { api, type Card } from "../lib/api";
import { useClientContext } from "../lib/ClientContext";
import { useCardsCounts, type CardCounts } from "../lib/useCardsCounts";
import { useSelectedMonth } from "../lib/useSelectedMonth";
import { stepUuid } from "../lib/stepUuid";

interface DriftStat {
  /** Counter aus `/api/cards/counts` (Server-Aggregat). */
  aggregate: CardCounts;
  /** Counter aus `/api/cards?type=all` clientseitig nachgezählt (Vergleichs-Quelle). */
  fromList: CardCounts;
  /** Welche Kategorien driften — leer wenn alles konsistent. */
  driftKeys: Array<keyof CardCounts>;
  loading: boolean;
  error: string | null;
}

export function WerkstattBestandPage({ modal }: { modal?: boolean } = {}) {
  const loc = useLocation();
  const nav = useNavigate();
  const background = (loc.state as { background?: Location } | null)?.background;
  function closeModal() {
    if (background) nav(-1);
    else nav("/board");
  }

  const { selectedClientId } = useClientContext();
  // Monat aus dem GETEILTEN Selector (Kanban/Inbox/Werkstatt teilen sich
  // diese Quelle, persistiert in sessionStorage + URL). Vor dem 2026-05-19-
  // Fix nahm die Bestand-View `new Date()` als Default und zeigte den
  // aktuellen Kalendermonat — wenn Thomas im Kanban April aktiv hatte,
  // sah der Werkstatt-Bestand trotzdem leere Mai-Daten. Step-UUID-Quell-
  // verschiebung sichtbar in Step-UUID b3109033-… für month=2026-05.
  const { month } = useSelectedMonth();

  const counts = useCardsCounts(month, selectedClientId);
  const drift = useDriftStat(month, selectedClientId, counts);

  const stepBase = `${month}-${selectedClientId ?? "default"}`;
  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "Aktive Karten gezählt (Server-Aggregat)",
      description:
        "GET /api/cards/counts liefert die kanonischen Bucket-Zahlen — eine "
        + "SQL-UNION über timesheets + expenses, Filter wie der Listen-Endpunkt "
        + "(state NOT IN superseded/archived, NOT LIKE merged_into_%). "
        + "Polling-Cadence 3 s, gleicher Rhythmus wie useKanbanPoll.",
      status: counts.total > 0 ? "done" : "pending",
      detail: (
        <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: "2px 16px", fontFamily: "monospace", fontSize: 14 }}>
          <span>Stunden:</span><strong>{counts.timesheet}</strong>
          <span>Diäten:</span><strong>{counts.expense}</strong>
          <span>Unklar:</span><strong>{counts.unknown}</strong>
          <span>Konflikt:</span><strong>{counts.conflict}</strong>
          <span style={{ borderTop: "1px solid #d1d5db", paddingTop: 2 }}>Total:</span>
          <strong style={{ borderTop: "1px solid #d1d5db", paddingTop: 2 }}>{counts.total}</strong>
        </div>
      ),
      uuid: stepUuid(stepBase, "bestand", 1),
    },
    {
      num: 2,
      title: "Drift-Check (Aggregat vs Card-Liste)",
      description:
        "Vergleicht den Server-Aggregat-Counter mit einer client-side "
        + "Zählung über GET /api/cards?type=all. Bei type=all sollten die "
        + "beiden Wege denselben Counter ergeben — Abweichung heißt ein "
        + "Aufrufer rechnet falsch, der Kanban-Tab-Drift-Bug von 2026-05-19 "
        + "wäre hier sofort sichtbar geworden.",
      status: drift.loading
        ? "active"
        : drift.error
          ? "error"
          : drift.driftKeys.length > 0
            ? "error"
            : "done",
      detail: drift.loading
        ? "lade type=all Liste…"
        : drift.error
          ? `Fehler beim Laden: ${drift.error}`
          : drift.driftKeys.length === 0
            ? (
              <span>
                ✓ Aggregat und type=all-Liste stimmen überein
                <span className="muted" style={{ marginLeft: 8 }}>
                  ({drift.aggregate.total} = {drift.fromList.total})
                </span>
              </span>
            )
            : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ color: "#dc2626", fontWeight: 600 }}>
                  ⚠ Drift in {drift.driftKeys.join(", ")} — Code-Pfad-Inkonsistenz
                </span>
                <table className="data-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>Bucket</th>
                      <th>Aggregat</th>
                      <th>Liste</th>
                      <th>Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(["timesheet", "expense", "unknown", "conflict", "total"] as const).map((k) => {
                      const a = drift.aggregate[k];
                      const l = drift.fromList[k];
                      const d = a - l;
                      return (
                        <tr key={k} style={d !== 0 ? { background: "#fef2f2" } : undefined}>
                          <td>{k}</td>
                          <td>{a}</td>
                          <td>{l}</td>
                          <td style={d !== 0 ? { color: "#dc2626", fontWeight: 600 } : undefined}>{d > 0 ? `+${d}` : d}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ),
      uuid: stepUuid(stepBase, "bestand", 2),
    },
    {
      num: 3,
      title: "Kanonische Quelle",
      description:
        "Jeder UI-Counter über Karten-Aggregate (Kanban-Tab-Badges, Mobile-"
        + "Tabs, Inbox-Badge, künftige Dashboard-Tiles) liest aus diesem "
        + "Aggregat-Endpoint. Andere Quellen = potentieller Drift-Bug. "
        + "Code-Pointer:",
      status: "done",
      detail: (
        <ul className="muted" style={{ fontSize: 12, margin: 0, paddingLeft: 18 }}>
          <li><code>hub/src/routes/cards.ts → GET /counts</code> (SQL-Aggregat, single source)</li>
          <li><code>frontend/src/lib/useCardsCounts.ts</code> (3s-Poll-Hook)</li>
          <li><code>frontend/src/pages/KanbanPage.tsx → typeCounts</code> (Tab-Badges)</li>
        </ul>
      ),
      uuid: stepUuid(stepBase, "bestand", 3),
    },
  ];

  const body = (
    <div className="werkstatt-page">
      <WerkstattBranchHeader activeBranch="bestand" modal={modal} />
      <p className="muted" style={{ margin: "0 0 12px 0", maxWidth: 820 }}>
        Glass-Box-Sicht auf das Karten-Aggregat. Zeigt was die UI-Counter ZEIGEN
        SOLLTEN — wenn ein Counter im Kanban davon abweicht, ist das ein
        Drift-Bug.
      </p>
      <p style={{ fontSize: 13, color: "#374151", margin: "0 0 12px 0" }}>
        <strong>Monat:</strong> <code>{month}</code>
        {" · "}
        <strong>Mandant:</strong> <code>{selectedClientId ?? "(kein Mandant gewählt)"}</code>
      </p>
      <PipelineStepList steps={steps} />
    </div>
  );

  if (modal) {
    return (
      <DetailModal dirty={false} onClose={closeModal}>
        {body}
      </DetailModal>
    );
  }
  return body;
}

function useDriftStat(month: string, clientId: string | null, aggregate: CardCounts): DriftStat {
  const [fromList, setFromList] = useState<CardCounts>(aggregate);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api.cards
      .list(month, "0", clientId ?? undefined, "all")
      .then((r) => {
        if (!alive) return;
        const cards: Card[] = r.cards ?? [];
        let timesheet = 0, expense = 0, unknown = 0, conflict = 0;
        for (const c of cards) {
          if (c.state === "pending_conflict") conflict++;
          if (c.kind === "timesheet") timesheet++;
          else if (c.kind === "expense") expense++;
          else unknown++;
        }
        setFromList({
          timesheet,
          expense,
          unknown,
          conflict,
          archived: 0,
          total: cards.length,
          // by_doc_type ist kein Drift-Vergleichs-Key (driftKeys unten) — die
          // Bestand-Ansicht prüft nur die kind/state-Zähler. Leeres Objekt reicht.
          by_doc_type: {},
        });
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message ?? String(e));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [month, clientId, aggregate.total]); // re-run when aggregate refreshes

  const driftKeys: Array<keyof CardCounts> = (
    ["timesheet", "expense", "unknown", "conflict", "total"] as const
  ).filter((k) => aggregate[k] !== fromList[k]);

  return { aggregate, fromList, driftKeys, loading, error };
}
